import { api } from "@/lib/api";
import { formatDate, formatRelativeTime } from "@/lib/utils";
import type { AdminServerLogEvent } from "@a7y/types";
import {
  Badge,
  Button,
  DescriptionDetails,
  DescriptionList,
  DescriptionTerm,
  Text,
} from "@a7y/tailwind-catalyst";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowLeft,
  Code,
  Globe,
  Info,
  MapPin,
  Monitor,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { CopyButton } from "@/components/CopyButton";

const EVENT_TYPE_BADGE_COLOR: Record<string, string> = {
  error: "red",
  warning: "amber",
};

const ENV_BADGE_COLOR: Record<string, string> = {
  production: "green",
  staging: "yellow",
  development: "blue",
};

function CopyableValue({ value, mono }: { value: string; mono?: boolean }) {
  return (
    <div className="flex items-center gap-2">
      {mono ? (
        <code className="rounded bg-zinc-100 dark:bg-zinc-700 px-1.5 py-0.5 text-sm">{value}</code>
      ) : (
        <span>{value}</span>
      )}
      <CopyButton value={value} label="value" />
    </div>
  );
}

function Section({
  title,
  icon,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg bg-white dark:bg-zinc-800 p-6 shadow-sm ring-1 ring-zinc-200 dark:ring-zinc-700">
      <div className="mb-4 flex items-center gap-2">
        <span className="text-zinc-950 dark:text-white">{icon}</span>
        <span className="text-base font-semibold text-zinc-950 dark:text-white">{title}</span>
      </div>
      {children}
    </div>
  );
}

function formatJson(raw: string | null): string | null {
  if (!raw) return null;
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

export function ErrorWarningDetailPage() {
  const { messageId } = useParams<{ messageId: string }>();
  const navigate = useNavigate();

  const { data: entry, isLoading, error } = useQuery<AdminServerLogEvent | null>({
    queryKey: ["error-warning-detail", messageId],
    queryFn: () => api.getErrorWarningDetail(messageId!),
    enabled: !!messageId,
  });

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 dark:border-zinc-600 border-t-blue-600" />
      </div>
    );
  }

  if (error || !entry) {
    return (
      <div className="px-4 py-2">
        <Button plain onClick={() => navigate("/admin/errors-warnings")}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Errors & Warnings
        </Button>
        <div className="mt-8 text-center">
          <AlertTriangle className="mx-auto h-12 w-12 text-gray-400" />
          <Text className="mt-2 text-gray-500 dark:text-zinc-400">Log entry not found</Text>
        </div>
      </div>
    );
  }

  const formattedData = formatJson(entry.data);
  const formattedErrorData = formatJson(entry.error_data);

  const hasGeoInfo = entry.country_ip || entry.city || entry.region || entry.region_code || entry.timezone || entry.continent || entry.postal_code || entry.lat != null || entry.lon != null || entry.colo || entry.is_eu_country != null;
  const hasUserAgent = entry.os || entry.browser || entry.browser_version || entry.manufacturer || entry.model || entry.os_version || entry.user_agent;
  const hasEventSection = entry.message || formattedData;


  return (
    <div className="px-4 py-2">
      {/* Header */}
      <div className="mb-8">
        <Button plain onClick={() => navigate("/admin/errors-warnings")} className="mb-4">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Errors & Warnings
        </Button>
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-red-100 dark:bg-red-900/30">
            <AlertTriangle className="h-6 w-6 text-red-600" />
          </div>
          <div className="min-w-0 flex-1">
            {entry.message_id && (
              <h1 className="text-lg font-semibold text-zinc-950 dark:text-white mb-1">{entry.message_id}</h1>
            )}
            <div className="flex items-center gap-2">
              {entry.source && (
                <Badge color="zinc">{entry.source}</Badge>
              )}
              {entry.env && (
                <Badge color={ENV_BADGE_COLOR[entry.env] ?? "zinc"}>{entry.env}</Badge>
              )}
              <Badge color={EVENT_TYPE_BADGE_COLOR[entry.event_type] ?? "zinc"}>
                {entry.event_type}
              </Badge>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-8">
        {/* Event (message + data) — first card */}
        {hasEventSection && (
          <Section title="Event" icon={<Code className="h-5 w-5" />}>
            {entry.message && (
              <div className="mb-4">
                <p className="mb-1 text-sm font-medium text-zinc-500 dark:text-zinc-400">Message</p>
                <CopyableValue value={entry.message} />
              </div>
            )}
            {formattedData && (
              <div>
                {entry.message && (
                  <p className="mb-1 text-sm font-medium text-zinc-500 dark:text-zinc-400">Data</p>
                )}
                <div className="flex items-start justify-between gap-2">
                  <pre className="w-full overflow-x-auto rounded-lg bg-zinc-100 dark:bg-zinc-900 p-4 text-sm text-zinc-800 dark:text-zinc-200">
                    <code>{formattedData}</code>
                  </pre>
                  <CopyButton value={formattedData} label="event data" />
                </div>
              </div>
            )}
          </Section>
        )}

        {/* Core Info */}
        <Section title="Core Info" icon={<Info className="h-5 w-5" />}>
          <DescriptionList>
            {entry.message_id && (
              <>
                <DescriptionTerm>Message ID</DescriptionTerm>
                <DescriptionDetails>
                  <CopyableValue value={entry.message_id} mono />
                </DescriptionDetails>
              </>
            )}
            {entry.id && (
              <>
                <DescriptionTerm>Session ID</DescriptionTerm>
                <DescriptionDetails>
                  <CopyableValue value={entry.id} mono />
                </DescriptionDetails>
              </>
            )}
            {entry.publish_time && (
              <>
                <DescriptionTerm>Timestamp</DescriptionTerm>
                <DescriptionDetails>
                  <div>
                    <CopyableValue value={formatDate(entry.publish_time)} />
                    <Text className="text-sm text-zinc-400">{formatRelativeTime(entry.publish_time)}</Text>
                  </div>
                </DescriptionDetails>
              </>
            )}
            <>
              <DescriptionTerm>Type</DescriptionTerm>
              <DescriptionDetails>
                <Badge color={EVENT_TYPE_BADGE_COLOR[entry.event_type] ?? "zinc"}>
                  {entry.event_type}
                </Badge>
              </DescriptionDetails>
            </>
            {entry.env && (
              <>
                <DescriptionTerm>Environment</DescriptionTerm>
                <DescriptionDetails>{entry.env}</DescriptionDetails>
              </>
            )}
            {entry.source && (
              <>
                <DescriptionTerm>Source</DescriptionTerm>
                <DescriptionDetails>{entry.source}</DescriptionDetails>
              </>
            )}
            {entry.path && (
              <>
                <DescriptionTerm>Path</DescriptionTerm>
                <DescriptionDetails>
                  <code className="rounded bg-zinc-100 dark:bg-zinc-700 px-1.5 py-0.5 text-sm">{entry.path}</code>
                </DescriptionDetails>
              </>
            )}
            {entry.origin_host && (
              <>
                <DescriptionTerm>Origin Host</DescriptionTerm>
                <DescriptionDetails>{entry.origin_host}</DescriptionDetails>
              </>
            )}
            {entry.user_id && (
              <>
                <DescriptionTerm>User ID</DescriptionTerm>
                <DescriptionDetails>
                  <CopyableValue value={entry.user_id} mono />
                </DescriptionDetails>
              </>
            )}
          </DescriptionList>
        </Section>

        {/* Error Data */}
        {formattedErrorData && (
          <Section title="Error Data" icon={<AlertTriangle className="h-5 w-5" />}>
            <div className="flex items-start justify-between gap-2">
              <pre className="w-full overflow-x-auto rounded-lg bg-red-50 dark:bg-red-900/20 p-4 text-sm text-red-800 dark:text-red-200">
                <code>{formattedErrorData}</code>
              </pre>
              <CopyButton value={formattedErrorData} label="error data" />
            </div>
          </Section>
        )}

        {/* Geo Info */}
        {hasGeoInfo && (
          <Section title="Geo Info" icon={<Globe className="h-5 w-5" />}>
            <DescriptionList>
              {entry.country_ip && (
                <>
                  <DescriptionTerm>Country (IP)</DescriptionTerm>
                  <DescriptionDetails>{entry.country_ip}</DescriptionDetails>
                </>
              )}
              {entry.city && (
                <>
                  <DescriptionTerm>City</DescriptionTerm>
                  <DescriptionDetails>{entry.city}</DescriptionDetails>
                </>
              )}
              {entry.region && (
                <>
                  <DescriptionTerm>Region</DescriptionTerm>
                  <DescriptionDetails>{entry.region}</DescriptionDetails>
                </>
              )}
              {entry.region_code && (
                <>
                  <DescriptionTerm>Region Code</DescriptionTerm>
                  <DescriptionDetails>{entry.region_code}</DescriptionDetails>
                </>
              )}
              {entry.timezone && (
                <>
                  <DescriptionTerm>Timezone</DescriptionTerm>
                  <DescriptionDetails>{entry.timezone}</DescriptionDetails>
                </>
              )}
              {entry.continent && (
                <>
                  <DescriptionTerm>Continent</DescriptionTerm>
                  <DescriptionDetails>{entry.continent}</DescriptionDetails>
                </>
              )}
              {entry.postal_code && (
                <>
                  <DescriptionTerm>Postal Code</DescriptionTerm>
                  <DescriptionDetails>{entry.postal_code}</DescriptionDetails>
                </>
              )}
              {entry.lat != null && entry.lon != null && (
                <>
                  <DescriptionTerm>Coordinates</DescriptionTerm>
                  <DescriptionDetails>
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-zinc-400" />
                      <a
                        href={`https://www.google.com/maps?q=${entry.lat},${entry.lon}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        {entry.lat}, {entry.lon}
                      </a>
                    </div>
                  </DescriptionDetails>
                </>
              )}
              {entry.lat != null && entry.lon == null && (
                <>
                  <DescriptionTerm>Latitude</DescriptionTerm>
                  <DescriptionDetails>{entry.lat}</DescriptionDetails>
                </>
              )}
              {entry.lon != null && entry.lat == null && (
                <>
                  <DescriptionTerm>Longitude</DescriptionTerm>
                  <DescriptionDetails>{entry.lon}</DescriptionDetails>
                </>
              )}
              {entry.colo && (
                <>
                  <DescriptionTerm>Colo</DescriptionTerm>
                  <DescriptionDetails>{entry.colo}</DescriptionDetails>
                </>
              )}
              {entry.is_eu_country != null && (
                <>
                  <DescriptionTerm>EU Country</DescriptionTerm>
                  <DescriptionDetails>
                    <Badge color={entry.is_eu_country ? "blue" : "zinc"}>
                      {entry.is_eu_country ? "Yes" : "No"}
                    </Badge>
                  </DescriptionDetails>
                </>
              )}
            </DescriptionList>
          </Section>
        )}

        {/* User Agent */}
        {hasUserAgent && (
          <Section title="User Agent" icon={<Monitor className="h-5 w-5" />}>
            <DescriptionList>
              {entry.os && (
                <>
                  <DescriptionTerm>OS</DescriptionTerm>
                  <DescriptionDetails>{entry.os}</DescriptionDetails>
                </>
              )}
              {entry.browser && (
                <>
                  <DescriptionTerm>Browser</DescriptionTerm>
                  <DescriptionDetails>{entry.browser}</DescriptionDetails>
                </>
              )}
              {entry.browser_version && (
                <>
                  <DescriptionTerm>Browser Version</DescriptionTerm>
                  <DescriptionDetails>{entry.browser_version}</DescriptionDetails>
                </>
              )}
              {entry.manufacturer && (
                <>
                  <DescriptionTerm>Manufacturer</DescriptionTerm>
                  <DescriptionDetails>{entry.manufacturer}</DescriptionDetails>
                </>
              )}
              {entry.model && (
                <>
                  <DescriptionTerm>Model</DescriptionTerm>
                  <DescriptionDetails>{entry.model}</DescriptionDetails>
                </>
              )}
              {entry.os_version && (
                <>
                  <DescriptionTerm>OS Version</DescriptionTerm>
                  <DescriptionDetails>{entry.os_version}</DescriptionDetails>
                </>
              )}
              {entry.user_agent && (
                <>
                  <DescriptionTerm>User Agent</DescriptionTerm>
                  <DescriptionDetails>
                    <code className="rounded bg-zinc-100 dark:bg-zinc-700 px-1.5 py-0.5 text-xs break-all">
                      {entry.user_agent}
                    </code>
                  </DescriptionDetails>
                </>
              )}
            </DescriptionList>
          </Section>
        )}

      </div>
    </div>
  );
}
